import React from "react";
import { cn } from "@/utils/shadcn";
import { useTheme } from "@/hooks/useTheme";
import { trackEvent } from "@/analytics";
import { WEB_SPECIFIC_EVENTS } from "@/analytics/events";
import { LAB } from "./tokens";

const SunGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 1v2M7 11v2M1 7h2M11 7h2M3 3l1.4 1.4M9.6 9.6L11 11M11 3L9.6 4.4M4.4 9.6L3 11"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <circle cx="7" cy="7" r="2.6" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const MoonGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path
      d="M12 8.6A5.6 5.6 0 015.4 2 5.6 5.6 0 107 13a5.6 5.6 0 005-4.4z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const SystemGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2.5" width="11" height="7.5" rx="1.4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M5 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const THEMES = [
  { value: "light" as const, glyph: SunGlyph, label: "Light" },
  { value: "dark" as const, glyph: MoonGlyph, label: "Dark" },
  { value: "system" as const, glyph: SystemGlyph, label: "System" },
];

/** Bordered mono button. Cycles light → dark → system, same as the old toggle. */
export const MsThemeToggle = ({ className }: { className?: string }) => {
  const { theme, setTheme } = useTheme();
  const current = THEMES.find((t) => t.value === theme) || THEMES[0];
  const Glyph = current.glyph;

  const cycle = () => {
    const next = THEMES[(THEMES.findIndex((t) => t.value === theme) + 1) % THEMES.length].value;
    trackEvent(WEB_SPECIFIC_EVENTS.THEME_TOGGLED, {
      theme: next,
      previous_theme: theme,
      source: "web",
    });
    setTheme(next);
  };

  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${current.label} (click to cycle)`}
      aria-label={`Theme: ${current.label}`}
      className={cn(
        LAB,
        "inline-flex min-h-[38px] items-center gap-[7px] border border-ms-line px-[11px] text-[9px] text-ms-mute transition-colors hover:text-ms-ink",
        className
      )}
    >
      <Glyph />
      <span className="hidden ms700:inline">{current.label}</span>
    </button>
  );
};
